import { isEmpty } from 'lodash'
import { change, destroy, stopSubmit } from 'redux-form'
import * as Effects from 'redux-saga/effects'
import { select } from 'redux-saga/effects'
import {
  IRequestUpdateRoom,
  IRoomWasUpdated,
  roomWasUpdated,
} from '../../actions/bookingCalendar/room/requestUpdateRoom'
import requestUpdateRoomModal from '../../actions/bookingCalendar/roomModal/requestUpdateRoomModal'
import { toggleRoomModalIsSaving } from '../../actions/bookingCalendar/roomModal/toggleRoomModal'
import { callFetch } from '../../actions/fetch'
import closeModal from '../../actions/Modals/closeModal'
import fireToastError from '../../actions/toasts/fireToastError'
import { Modals } from '../../containers/Modals/ModalContainer'
import { database } from '../../database'
import i18n from '../../i18n'
import { updateRoom as requestUpdate } from '../../models/Room'
import { RoomActions } from '../../reducers/bookingCalendar/actionNames'
import { getRoomIdFromOpenModal } from '../../reducers/bookingCalendar/selectors/getModalOpen'
import {
  findRoomById,
  getNextId,
  getPrevId,
} from '../../reducers/bookingCalendar/selectors/rooms'
import { ActionNames } from '../../utils/FetchActionsName'
import fetchActionsName from '../../utils/FetchActionsName/fetchActionsName'
import { Mixpanel } from '../../utils/Mixpanel'
import { mapRoomTypeToEventString } from './createRoom'
import { getUserId } from '../../reducers/userAccount/selectors/userAccount'

function* updateRoom(action: IRequestUpdateRoom) {
  try {
    yield Effects.put(toggleRoomModalIsSaving(true))
    const actionNames = fetchActionsName(ActionNames.UpdateRoom)
    const [{ calendarId, service }, selectedRoom] = yield Effects.all([
      Effects.take(actionNames.output),
      Effects.select(getRoomIdFromOpenModal),
      Effects.put(
        callFetch(actionNames.input, actionNames.output, requestUpdate),
      ),
    ])

    const room = yield Effects.select(state =>
      findRoomById(state, {
        roomId: selectedRoom ? selectedRoom : action.roomId,
      }),
    )
    const userId = yield select(getUserId)
    const rooms = yield Effects.call(
      service,
      calendarId,
      selectedRoom ? selectedRoom : action.roomId,
      {
        additionalEquipment: room.additionalEquipment,
        size: room.size,
        ...action.room,
        modified: true,
      },
    )
    for (const room of rooms) {
      Mixpanel.people.increment(userId, 'Rooms Edited', 1)
      Mixpanel.track('Room Form Saved', {
        'Room ID': Number(room.id),
        'Room Name': room.name,
        'Room Name Length': room.name.length,
        'Room Type': mapRoomTypeToEventString(room.type),
        'Room No. Of People': Number(room.roomSize),
        'Room Extra Beds': Number(room.additionalBeds),
        'Room Single Beds': Number(room.singleBed),
        'Room Double Beds': Number(room.doubleBed),
        'Room Display Order': Number(room.position),
        'Room Custom Color': !!room.customColor,
        'Room Description Length': room.description
          ? Number(room.description.length)
          : undefined,
        'Room Additional equipment Length': room.additionalEquipment
          ? Number(room.additionalEquipment.length)
          : undefined,
        'Room Size': room.size ? Number(room.size) : undefined,
      })

      yield Effects.put(roomWasUpdated(room))
    }

    if (!action.room.dontClose) {
      yield destroy(action.formName)
      yield Effects.put(closeModal(Modals.EditRoom))
    }

    yield Effects.put(change(action.formName, 'dontClose', false))
  } catch (e) {
    yield stopSubmit(action.formName, e)
    console.error(e)
    yield Effects.put(fireToastError(i18n.t('error_server_response_2')))
  } finally {
    yield Effects.put(toggleRoomModalIsSaving(false))
  }
}

function* putRoomIntoDb(action: IRoomWasUpdated) {
  const { room } = action
  if (!isEmpty(room.customColor)) {
    ;(room as any).savedColor = room.customColor.rgb().toString()
  }
  yield database.rooms.update(action.room.id, action.room)
}

function nextRoom() {
  const nextRoom = getNextId

  return function*(action) {
    const roomId = yield Effects.select(getRoomIdFromOpenModal)
    const nextRoomId = yield Effects.select(
      nextRoom(roomId ? roomId : action.roomId),
    )
    yield Effects.put(requestUpdateRoomModal(nextRoomId))
  }
}

function prevRoom() {
  const prevRoom = getPrevId

  return function*(action) {
    const roomId = yield Effects.select(getRoomIdFromOpenModal)
    const prevRoomId = yield Effects.select(
      prevRoom(roomId ? roomId : action.roomId),
    )
    yield Effects.put(requestUpdateRoomModal(prevRoomId))
  }
}

export default function*() {
  yield Effects.takeLatest(RoomActions.RequestUpdateRoom, updateRoom)
  yield Effects.takeEvery(RoomActions.RoomWasUpdated, putRoomIntoDb)
  yield Effects.takeEvery(RoomActions.RequestNext, nextRoom())
  yield Effects.takeEvery(RoomActions.RequestPrev, prevRoom())
}
